/**
 * A-LIGN Audit Kickoff Inline Summary — embeds on /soc2-readiness.
 *
 * Pulls /api/align-kickoff/checklist (lightweight) and renders a compact
 * "kickoff-ready" status pill + checklist progress + CTA to the full page.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { CheckCircle2, AlertTriangle, Mail, ArrowRight, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AlignKickoffInline() {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) { setLoaded(true); return; }
      const r = await fetch(`${API}/api/align-kickoff/checklist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setData(await r.json());
    } finally { setLoaded(true); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  if (!loaded || !data) return null;
  const s = data.summary || {};
  const ready = s.ready_to_kickoff;
  const verifiedOk = s.ok + s.evidence_only;

  return (
    <Card
      className={`bg-gradient-to-br ${ready ? 'from-emerald-500/10 via-slate-900/40 to-cyan-500/10 border-emerald-500/30' : 'from-amber-500/10 via-slate-900/40 to-rose-500/10 border-amber-500/30'} mb-8`}
      data-testid="align-kickoff-inline"
    >
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className={`inline-flex w-12 h-12 rounded-xl bg-cyan-500 bg-gradient-to-br ${ready ? 'from-emerald-500 to-cyan-500' : 'from-amber-500 to-rose-500'} items-center justify-center shadow-lg shrink-0`}>
              {ready ? <CheckCircle2 className="w-6 h-6 text-white" /> : <AlertTriangle className="w-6 h-6 text-white" />}
            </div>
            <div>
              <Badge className={`${ready ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-200' : 'bg-amber-500/15 border-amber-500/30 text-amber-200'} text-[10px] font-mono tracking-widest mb-1`}>
                <Mail className="w-3 h-3 mr-1 inline" /> A-LIGN audit kickoff
              </Badge>
              <h3 className="text-lg font-bold text-white" data-testid="align-inline-headline">
                {ready ? 'Ready to kick off the audit' : `${verifiedOk}/${s.total} checks verified · ${s.below_threshold} below threshold`}
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Pre-audit checklist · auditor email draft · signed evidence bundle
              </p>
            </div>
          </div>
          <div className="md:ml-auto flex items-center gap-3">
            <div className="text-right">
              <div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest">Checklist</div>
              <div className="font-mono text-sm text-white">
                <span className="text-emerald-300">{s.ok}</span> ok ·{' '}
                <span className="text-cyan-300">{s.evidence_only}</span> evidence ·{' '}
                <span className="text-slate-400">{s.manual}</span> manual
              </div>
            </div>
            <Link
              to="/admin/align-kickoff"
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold px-4 py-2 rounded-full text-sm transition-colors"
              data-testid="align-inline-cta"
            >
              Open kickoff <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        {!ready && s.below_threshold > 0 && (
          <div className="mt-3 text-xs text-amber-300 flex items-center gap-1.5">
            <Clock className="w-3 h-3" /> {s.below_threshold} item{s.below_threshold !== 1 ? 's' : ''} below target — open the kickoff page for action items.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
